import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs, listSubjectConfigs, listExamMarksForClass } from "../../api/examApi";
import "../Attendance/Attendance.css";

const classApi = createLookupApi("classes");
const examApi = createLookupApi("exams");
const academicYearApi = createLookupApi("academic-years");

/**
 * Direct port of digicampusbd's `exam-report-section-wise-un-inputted-subjects`
 * (`ExamReportService#sectionWiseUninputtedMarkSubjects`) — for each selected
 * class's every class-configuration, which of its configured subjects still
 * has no marks entered for one exam. Built entirely on already-existing
 * endpoints (no new backend code): for each selected class, its own
 * `examConfigId` for the chosen exam name is looked up via `GET
 * /exam-config` (already returns `examId` per config), then that
 * `examConfigId`'s configured subjects (`GET /exam-config/subject-configs`,
 * already built for `ExamSubjectConfigPage`) are compared against the
 * distinct subjects actually present in `GET /api/exam-marks/for-class`
 * (new last pass, for the failed-subject-detail reports) for each of that
 * class's class-configurations.
 *
 * One documented simplification: the old app's subject list is scoped per
 * *group* (a class can have different subject lists per group, e.g.
 * Science/Commerce/Arts), so its response breaks "remaining subjects" down
 * by group. This app's exam-subject-configuration has no group-scoping at
 * all (the same pre-existing modeling gap noted for the failed-subject-detail
 * reports) — one configured subject list per class+exam, not per group — so
 * this page reports one row per class-configuration instead of one row per
 * class-configuration-and-group. Identical result for institutes where every
 * group in a class takes the same subjects (the common case).
 */
export default function ExamUninputtedSubjectsPage() {
  const [classes, setClasses] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([classApi.list(), examApi.list(), academicYearApi.list(), listClassConfigurations()]).then(
      ([c, e, y, cc]) => {
        setClasses(c);
        if (c.length > 0) setSelectedClassIds([String(c[0].id)]);
        setExams(e);
        setExamId(e.length > 0 ? String(e[0].id) : "");
        setAcademicYears(y);
        setAcademicYear(y.length > 0 ? y[0].name : "");
        setClassConfigs(cc);
      }
    );
  }, []);

  const handleSearch = async () => {
    if (selectedClassIds.length === 0 || !examId || !academicYear) return;
    setLoading(true);
    setError("");
    try {
      const results = [];
      for (const classIdStr of selectedClassIds) {
        const classId = Number(classIdStr);
        // eslint-disable-next-line no-await-in-loop
        const examConfigs = await listExamConfigs(classId);
        const matchingConfig = examConfigs.find((ec) => String(ec.examId) === examId);
        if (!matchingConfig) continue;

        // eslint-disable-next-line no-await-in-loop
        const subjectConfigs = await listSubjectConfigs(matchingConfig.examConfigId);
        const configuredSubjects = subjectConfigs.map((sc) => ({ id: sc.subjectId, name: sc.subjectName }));

        const configsForClass = classConfigs.filter((cc) => cc.classId === classId);
        for (const cc of configsForClass) {
          // eslint-disable-next-line no-await-in-loop
          const marks = await listExamMarksForClass(matchingConfig.examConfigId, cc.id, academicYear);
          const inputtedIds = new Set(marks.map((m) => m.subjectId));
          const remaining = configuredSubjects.filter((s) => !inputtedIds.has(s.id));
          results.push({
            classConfigId: cc.id,
            sectionName: cc.classConfigName,
            totalSubjects: configuredSubjects.length,
            inputtedSubjects: configuredSubjects.length - remaining.length,
            remainingSubjects: remaining.length,
            remainingSubjectNames: remaining.map((s) => s.name).join(", ") || "—",
          });
        }
      }
      setRows(results);
    } catch (err) {
      setError("Failed to load uninputted-subject summary.");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { key: "sectionName", label: "Section" },
    { key: "totalSubjects", label: "Total Subjects" },
    { key: "inputtedSubjects", label: "Inputted Subjects" },
    { key: "remainingSubjects", label: "Remaining Subjects" },
    { key: "remainingSubjectNames", label: "Remaining Subject Names" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Section Wise Un-inputted Subject List</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters">
        <label>
          Class(es)
          <select
            multiple
            value={selectedClassIds}
            onChange={(e) => setSelectedClassIds(Array.from(e.target.selectedOptions, (o) => o.value))}
          >
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examId} onChange={(e) => setExamId(e.target.value)}>
            {exams.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>

      <DataTable columns={columns} rows={rows} />
      {!loading && rows.length === 0 && <p>Select class(es), an exam, and an academic year, then click Search.</p>}
    </MainLayout>
  );
}
